/* Recipely theme scripts — no dependencies. */
(() => {
  'use strict';

  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const theme = window.theme || {};

  /* Utilities ------------------------------------------------------------ */
  const toast = (message) => {
    const el = $('[data-toast]');
    if (!el) return;
    el.textContent = message;
    el.classList.add('is-visible');
    clearTimeout(el._t);
    el._t = setTimeout(() => el.classList.remove('is-visible'), 2600);
  };

  const lockScroll = (lock) => document.body.classList.toggle('is-locked', lock);

  const trapFocus = (container) => {
    const focusable = $$('a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])', container)
      .filter((el) => el.offsetParent !== null);
    if (focusable.length) focusable[0].focus();
  };

  /* Drawers (mobile menu, cart) ----------------------------------------- */
  const openDrawer = (drawer, trigger) => {
    if (!drawer) return;
    drawer._trigger = trigger || document.activeElement;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    lockScroll(true);
    setTimeout(() => trapFocus(drawer), 50);
  };

  const closeDrawer = (drawer) => {
    if (!drawer || !drawer.classList.contains('is-open')) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    lockScroll(false);
    if (drawer._trigger) drawer._trigger.focus();
  };

  const initMenu = () => {
    const menu = $('[data-mobile-menu]');
    if (!menu) return;
    $$('[data-menu-open]').forEach((btn) => btn.addEventListener('click', () => {
      btn.setAttribute('aria-expanded', 'true');
      openDrawer(menu, btn);
    }));
    $$('[data-menu-close]', menu).forEach((btn) => btn.addEventListener('click', () => {
      $$('[data-menu-open]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
      closeDrawer(menu);
    }));
  };

  /* Header -------------------------------------------------------------- */
  const initHeader = () => {
    const header = $('[data-header]');
    if (!header) return;
    const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    document.documentElement.style.setProperty('--header-height', `${header.offsetHeight}px`);
  };

  /* Announcement rotator ------------------------------------------------ */
  const initAnnouncements = () => {
    $$('[data-announcement-rotator]').forEach((root) => {
      const items = $$('.announcement-bar__message', root);
      if (items.length < 2) return;
      let i = 0;
      setInterval(() => {
        items[i].classList.remove('is-active');
        i = (i + 1) % items.length;
        items[i].classList.add('is-active');
      }, parseInt(root.dataset.interval, 10) || 5000);
    });
  };

  /* Sliders ------------------------------------------------------------- */
  const initSliders = (root = document) => {
    $$('[data-slider]', root).forEach((slider) => {
      if (slider._init) return;
      slider._init = true;
      const track = $('[data-slider-track]', slider);
      const prev = $('[data-slider-prev]', slider);
      const next = $('[data-slider-next]', slider);
      if (!track) return;

      const step = () => {
        const slide = track.firstElementChild;
        if (!slide) return track.clientWidth;
        const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        return slide.getBoundingClientRect().width + gap;
      };
      const update = () => {
        const max = track.scrollWidth - track.clientWidth - 2;
        slider.classList.toggle('is-static', max <= 0);
        if (prev) prev.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= max;
      };

      prev && prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
      next && next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
      track.addEventListener('scroll', () => window.requestAnimationFrame(update), { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  };

  /* Search modal -------------------------------------------------------- */
  const initSearch = () => {
    const modal = $('[data-search-modal]');
    if (!modal) return;
    const open = () => {
      modal.hidden = false;
      lockScroll(true);
      setTimeout(() => $('input[type="search"]', modal).focus(), 30);
    };
    const close = () => { modal.hidden = true; lockScroll(false); };
    $$('[data-search-open]').forEach((b) => b.addEventListener('click', open));
    $$('[data-search-close]', modal).forEach((b) => b.addEventListener('click', close));
    modal._close = close;
  };

  /* Auto-submitting selects (localization, sort, filters) --------------- */
  const initAutosubmit = () => {
    document.addEventListener('change', (e) => {
      const el = e.target;
      if (!el.matches('[data-autosubmit]')) return;
      const form = el.form || el.closest('form');
      if (!form) return;
      if (form.matches('[data-filter-form]') || el.getAttribute('form') === 'CollectionFilters') {
        // Skip auto-submit inside the mobile filter drawer; the Apply button handles it.
        if (window.matchMedia('(max-width: 989px)').matches && el.closest('[data-filters]')) return;
      }
      form.requestSubmit ? form.requestSubmit() : form.submit();
    });

    // Strip empty params from filter form submissions for clean URLs.
    $$('[data-filter-form]').forEach((form) => {
      form.addEventListener('formdata', (e) => {
        for (const [key, value] of Array.from(e.formData.entries())) {
          if (value === '') e.formData.delete(key);
        }
      });
    });
  };

  /* Filters drawer (mobile) --------------------------------------------- */
  const initFilters = () => {
    const filters = $('[data-filters]');
    if (!filters) return;
    $$('[data-filters-open]').forEach((b) => b.addEventListener('click', () => {
      filters.classList.add('is-open');
      lockScroll(true);
    }));
    $$('[data-filters-close]', filters).forEach((b) => b.addEventListener('click', () => {
      filters.classList.remove('is-open');
      lockScroll(false);
    }));
  };

  /* Cart ---------------------------------------------------------------- */
  const Cart = {
    drawer: () => $('[data-cart-drawer]'),
    useDrawer: () => theme.cartType === 'drawer' && !!$('[data-cart-drawer]'),

    updateCount(count) {
      $$('[data-cart-count]').forEach((el) => {
        el.textContent = count;
        el.classList.toggle('is-empty', count === 0);
        el.classList.remove('is-bumped');
        void el.offsetWidth;
        el.classList.add('is-bumped');
      });
    },

    renderDrawer(html) {
      const drawer = this.drawer();
      if (!drawer || !html) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = $('[data-cart-drawer] .drawer__panel', doc);
      const panel = $('.drawer__panel', drawer);
      if (fresh && panel) panel.replaceWith(fresh);
      this.bindDrawer();
      this.loadUpsell();
    },

    async loadUpsell() {
      const slot = $('[data-cart-upsell]');
      if (!slot || slot.dataset.loaded) return;
      slot.dataset.loaded = 'true';
      try {
        const html = await (await fetch(slot.dataset.url)).text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const inner = $('.cart-upsell__inner', doc);
        if (inner) slot.appendChild(inner);
      } catch (_) { /* recommendations are optional */ }
    },

    async add(form) {
      const formData = new FormData(form);
      if (this.useDrawer()) formData.append('sections', 'cart-drawer');
      formData.append('sections_url', window.location.pathname);
      const res = await fetch(`${theme.routes.cartAdd}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || theme.strings.error);
      return data;
    },

    async change(line, quantity) {
      const body = { line, quantity };
      if (this.useDrawer()) body.sections = ['cart-drawer'];
      const res = await fetch(`${theme.routes.cartChange}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || theme.strings.error);
      return data;
    },

    open() {
      const drawer = this.drawer();
      if (drawer) openDrawer(drawer);
    },

    bindDrawer() {
      const drawer = this.drawer();
      if (!drawer) return;
      $$('[data-cart-close]', drawer).forEach((btn) => {
        btn.onclick = () => closeDrawer(drawer);
      });
    },

    init() {
      this.bindDrawer();
      this.loadUpsell();

      // Open the drawer from header cart icon
      $$('[data-cart-drawer-toggle]').forEach((link) => link.addEventListener('click', (e) => {
        if (!this.useDrawer()) return;
        e.preventDefault();
        this.open();
      }));

      // Product forms (main form, card quick add, cart upsell). Delegated so
      // forms loaded later (recommendations) work too.
      document.addEventListener('submit', async (e) => {
        const form = e.target.closest('[data-product-form]');
        if (!form) return;
        e.preventDefault();
        const button = $('[data-add-button]', form);
        const error = $('[data-form-error]', form);
        const isQuick = form.hasAttribute('data-quick-add');
        if (error) error.hidden = true;
        if (button) button.classList.add('is-loading');
        try {
          const data = await this.add(form);
          const cart = await (await fetch(`${theme.routes.cart}.js`)).json();
          this.updateCount(cart.item_count);
          if (isQuick && button) {
            button.classList.add('is-added');
            setTimeout(() => button.classList.remove('is-added'), 1600);
          }
          if (this.useDrawer()) {
            this.renderDrawer(data.sections && data.sections['cart-drawer']);
            this.open();
          } else {
            window.location.href = theme.routes.cart;
          }
        } catch (err) {
          if (error) { error.textContent = err.message; error.hidden = false; }
          else toast(err.message);
        } finally {
          if (button) button.classList.remove('is-loading');
        }
      });

      // Quantity / remove buttons (drawer + cart page), delegated
      document.addEventListener('click', async (e) => {
        const btn = e.target.closest('[data-cart-qty]');
        if (!btn) return;
        e.preventDefault();
        await this.handleChange(btn.closest('.cart-item'), parseInt(btn.dataset.line, 10), parseInt(btn.dataset.cartQty, 10));
      });
      document.addEventListener('change', async (e) => {
        const input = e.target.closest('[data-line-input]');
        if (!input) return;
        await this.handleChange(input.closest('.cart-item'), parseInt(input.dataset.lineInput, 10), Math.max(0, parseInt(input.value, 10) || 0));
      });

      // Cart note
      const note = $('[data-cart-note]');
      if (note) {
        let t;
        note.addEventListener('input', () => {
          clearTimeout(t);
          t = setTimeout(() => fetch(`${theme.routes.cart}/update.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ note: note.value }),
          }), 500);
        });
      }
    },

    async handleChange(item, line, quantity) {
      if (item) item.classList.add('is-updating');
      try {
        const data = await this.change(line, quantity);
        this.updateCount(data.item_count);
        if ($('[data-cart-page]')) {
          window.location.reload();
          return;
        }
        this.renderDrawer(data.sections && data.sections['cart-drawer']);
      } catch (err) {
        toast(err.message);
        if (item) item.classList.remove('is-updating');
      }
    },
  };

  /* Product page -------------------------------------------------------- */
  const initProduct = () => {
    $$('[data-product-section]').forEach((section) => {
      const jsonEl = $('[data-product-json]', section);
      const data = jsonEl ? JSON.parse(jsonEl.textContent) : null;
      const gallery = $('[data-gallery]', section);

      // Gallery
      const showMedia = (id) => {
        if (!gallery || !id) return;
        const target = $(`.product__media[data-media-id="${id}"]`, gallery);
        if (!target) return;
        $$('.product__media', gallery).forEach((m) => {
          const active = m === target;
          m.classList.toggle('is-active', active);
          if (!active) $$('video', m).forEach((v) => v.pause());
        });
        $$('[data-thumb]', gallery).forEach((t) => t.classList.toggle('is-active', t.dataset.thumb === String(id)));
      };

      if (gallery) {
        $$('[data-thumb]', section).forEach((t) => t.addEventListener('click', () => {
          showMedia(t.dataset.thumb);
          if (!gallery.contains(t)) gallery.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }));
        const step = (dir) => {
          const media = $$('.product__media', gallery);
          const idx = media.findIndex((m) => m.classList.contains('is-active'));
          const nextIdx = (idx + dir + media.length) % media.length;
          showMedia(media[nextIdx].dataset.mediaId);
        };
        const prev = $('[data-gallery-prev]', gallery);
        const next = $('[data-gallery-next]', gallery);
        prev && prev.addEventListener('click', () => step(-1));
        next && next.addEventListener('click', () => step(1));

        // Swipe on touch devices
        const viewer = $('.product__viewer', gallery);
        let startX = null;
        viewer.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX; }, { passive: true });
        viewer.addEventListener('touchend', (e) => {
          if (startX === null) return;
          const dx = e.changedTouches[0].clientX - startX;
          if (Math.abs(dx) > 40) step(dx < 0 ? 1 : -1);
          startX = null;
        });
      }

      // Variants
      const selects = $$('[data-option-index]', section);
      const idInput = $('[data-variant-id]', section);
      const addBtn = $('[data-add-button]', section);
      const addLabel = $('[data-add-label]', section);
      const priceEl = $('.product__price [data-price]', section);

      if (data && selects.length) {
        selects.forEach((select) => select.addEventListener('change', () => {
          const options = selects.map((s) => s.value);
          const variant = data.variants.find((v) => v.options.every((o, i) => o === options[i]));

          if (!variant) {
            if (addBtn) addBtn.disabled = true;
            if (addLabel) addLabel.textContent = theme.strings.unavailable;
            const addPriceEl = $('[data-add-price]', section);
            if (addPriceEl) addPriceEl.hidden = true;
            return;
          }

          if (idInput) idInput.value = variant.id;
          if (addBtn) addBtn.disabled = !variant.available;
          if (addLabel) addLabel.textContent = variant.available ? theme.strings.addToCart : theme.strings.soldOut;

          const regular = variant.is_free ? data.free_label : variant.price;
          const compare = variant.compare_at_price ? `<s class="price__compare">${variant.compare_at_price}</s>` : '';
          const save = data.show_savings && variant.savings > 0 ? `<span class="price__save">${data.save_label.replace('__P__', variant.savings)}</span>` : '';
          if (priceEl) {
            priceEl.classList.toggle('price--on-sale', !!variant.compare_at_price);
            priceEl.innerHTML = `<span class="price__regular">${regular}</span>${compare}${save}`;
          }
          const addPrice = $('[data-add-price]', section);
          if (addPrice) { addPrice.textContent = regular; addPrice.hidden = !variant.available; }
          const sticky = $('[data-sticky-atc]', section);
          if (sticky) {
            const stickyPrice = $('[data-price]', sticky);
            if (stickyPrice) stickyPrice.innerHTML = `<span class="price__regular">${regular}</span>${compare}`;
            const stickyBtn = $('[data-sticky-atc-button]', sticky);
            if (stickyBtn) stickyBtn.disabled = !variant.available;
            const stickyLabel = $('[data-sticky-label]', sticky);
            if (stickyLabel) stickyLabel.textContent = variant.available ? theme.strings.addToCart : theme.strings.soldOut;
          }

          if (variant.featured_media_id) showMedia(variant.featured_media_id);

          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url.toString());
        }));
      }

      // Quantity buttons
      $$('[data-quantity]', section).forEach((wrap) => {
        const input = $('input', wrap);
        $('[data-qty-minus]', wrap).addEventListener('click', () => { input.value = Math.max(1, (parseInt(input.value, 10) || 1) - 1); });
        $('[data-qty-plus]', wrap).addEventListener('click', () => { input.value = (parseInt(input.value, 10) || 1) + 1; });
      });
    });

    // "Get the recipe" in the instructions section scrolls to the buy form
    $$('[data-scroll-to-buy]').forEach((link) => link.addEventListener('click', (e) => {
      const form = $('[data-product-form]');
      if (!form) return;
      e.preventDefault();
      form.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const btn = $('[data-add-button]', form);
      setTimeout(() => btn && btn.focus({ preventScroll: true }), 500);
    }));

    // Copy link
    $$('[data-copy-link]').forEach((btn) => btn.addEventListener('click', async () => {
      try {
        await navigator.clipboard.writeText(btn.dataset.copyLink);
        toast(theme.strings.copied);
      } catch (_) { /* clipboard unavailable */ }
    }));
  };

  /* Sticky add-to-cart bar ---------------------------------------------- */
  const initStickyAtc = () => {
    $$('[data-product-section]').forEach((section) => {
      const bar = $('[data-sticky-atc]', section);
      const form = $('form[data-product-form]:not([data-quick-add])', section);
      const mainButton = form && $('[data-add-button]', form);
      if (!bar || !mainButton || !('IntersectionObserver' in window)) return;

      const setVisible = (visible) => {
        bar.classList.toggle('is-visible', visible);
        bar.setAttribute('aria-hidden', visible ? 'false' : 'true');
        $('[data-sticky-atc-button]', bar).tabIndex = visible ? 0 : -1;
        document.body.classList.toggle('has-sticky-atc', visible);
      };

      // Show once the main button has scrolled up out of view; hide when it's visible again.
      new IntersectionObserver(([entry]) => {
        setVisible(!entry.isIntersecting && entry.boundingClientRect.top < 0);
      }).observe(mainButton);

      $('[data-sticky-atc-button]', bar).addEventListener('click', () => {
        if (form.requestSubmit) form.requestSubmit();
        else mainButton.click();
      });
    });
  };

  /* Product recommendations --------------------------------------------- */
  const initRecommendations = () => {
    $$('[data-recommendations]').forEach(async (section) => {
      if (section.querySelector('.slider')) return;
      try {
        const res = await fetch(section.dataset.url);
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = $('[data-recommendations]', doc);
        if (fresh && fresh.innerHTML.trim()) {
          section.innerHTML = fresh.innerHTML;
          initSliders(section);
        }
      } catch (_) { /* leave empty */ }
    });
  };

  /* Newsletter popup ---------------------------------------------------- */
  const initPopup = () => {
    const popup = $('[data-newsletter-popup]');
    if (!popup) return;
    const KEY = 'recipely-popup-dismissed';
    const close = () => {
      popup.hidden = true;
      lockScroll(false);
      try { localStorage.setItem(KEY, String(Date.now())); } catch (_) { /* storage blocked */ }
    };
    $$('[data-popup-close]', popup).forEach((b) => b.addEventListener('click', close));
    popup._close = close;

    // Keep the popup open after a form submit (shows the success message)
    if (window.location.search.includes('customer_posted=true') || window.location.hash === '#popup') {
      popup.hidden = false;
      return;
    }

    let dismissedAt = 0;
    try { dismissedAt = parseInt(localStorage.getItem(KEY), 10) || 0; } catch (_) { /* storage blocked */ }
    const sevenDays = 7 * 24 * 60 * 60 * 1000;
    if (popup.dataset.designMode !== 'true' && Date.now() - dismissedAt < sevenDays) return;

    setTimeout(() => { popup.hidden = false; }, parseInt(popup.dataset.delay, 10) || 6000);
  };

  /* Customer login: toggle password recovery ---------------------------- */
  const initRecover = () => {
    const login = $('#Login');
    const recover = $('#recover');
    if (!login || !recover) return;
    const show = (showRecover) => { login.hidden = showRecover; recover.hidden = !showRecover; };
    if (window.location.hash === '#recover') show(true);
    $$('[data-toggle-recover]').forEach((link) => link.addEventListener('click', (e) => {
      e.preventDefault();
      show(recover.hidden);
    }));
  };

  /* Address forms ------------------------------------------------------- */
  const initAddresses = () => {
    $$('select[data-default]').forEach((select) => {
      if (select.dataset.default) select.value = select.dataset.default;
    });
    $$('form[data-confirm]').forEach((form) => form.addEventListener('submit', (e) => {
      if (!window.confirm(form.dataset.confirm)) e.preventDefault();
    }));
  };

  /* Global keyboard ----------------------------------------------------- */
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    $$('.drawer.is-open').forEach(closeDrawer);
    const filters = $('[data-filters].is-open');
    if (filters) { filters.classList.remove('is-open'); lockScroll(false); }
    const search = $('[data-search-modal]');
    if (search && !search.hidden && search._close) search._close();
    const popup = $('[data-newsletter-popup]');
    if (popup && !popup.hidden && popup._close) popup._close();
  });

  /* Theme editor support ------------------------------------------------ */
  document.addEventListener('shopify:section:load', (e) => {
    initSliders(e.target);
    if (e.target.querySelector('[data-header]')) { initHeader(); initMenu(); }
    if (e.target.querySelector('[data-newsletter-popup]')) initPopup();
    if (e.target.querySelector('[data-product-section]')) initProduct();
  });

  /* Boot ---------------------------------------------------------------- */
  const boot = () => {
    initHeader();
    initMenu();
    initAnnouncements();
    initSliders();
    initSearch();
    initAutosubmit();
    initFilters();
    Cart.init();
    initProduct();
    initStickyAtc();
    initRecommendations();
    initPopup();
    initRecover();
    initAddresses();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
